var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Euler, Matrix4, Quaternion, Vector3, Box3 } from "flavr-math";
import { convertToNumber3 } from "flavr-common";
import { RequestCollisionShapeAABBMessage, ResponseCollisionShapeAABBMessage, RequestRaycastClosestIntersectionMessage, ResponseRaycastClosestIntersectionMessage } from "@messages/selection";
import { modelPool } from "@modules/model";
const TEMP_ORIENTATION = new Quaternion();
const TEMP_SCALE = new Vector3();
const TEMP_UNUSED_POSITION = new Vector3();
const TEMP_EULER = new Euler();
const TEMP_DELTA_YAW_QUAT = new Quaternion();
const TEMP_CONTROLLER_QUAT = new Quaternion();
const TEMP_VECTOR_Y = new Vector3(0, 1, 0);
/**
 * Performs a downward raycast from the held model onto a drop-off target. If the area has no
 * `dropOffTargetUid`, the area itself is the target. A hit is required before the preview is shown.
 */
export class DropOffTargetManager {
    constructor(connection) {
        this.connection = connection;
    }
    computeDropOffWorldTransform(modelUID, dropOffTargetUID, initialOrientation, controllerUID) {
        return __awaiter(this, void 0, void 0, function* () {
            const modelTransform = this.retrieveModelWorldTransform(modelUID);
            if (modelTransform === undefined)
                return undefined;
            const raycastOrigin = new Vector3();
            modelTransform.decompose(raycastOrigin, new Quaternion(), new Vector3());
            // If no dropOffTargetUid is specified then the pick-up object can be dropped off in place
            return yield this.computeDropOffPointWithTarget(modelUID, modelTransform, dropOffTargetUID, raycastOrigin, initialOrientation, controllerUID);
        });
    }
    /// Private target transform methods ///
    computeDropOffPointWithTarget(modelUID, modelTransform, targetUID, origin, initialOrientation, controllerUID) {
        return __awaiter(this, void 0, void 0, function* () {
            const raycastResult = yield this.performDownwardsRaycast(origin, targetUID);
            if (raycastResult === undefined)
                return undefined;
            // Modify y-position of hitPoint, such that drop-off model doesn't stick inside the surface
            const hitPoint = raycastResult.hitPoint;
            hitPoint.y += yield this.getHalfBoundingHeight(modelUID);
            return this.computeDropOffTransform(modelTransform, hitPoint, initialOrientation, controllerUID);
        });
    }
    /// Private transform helper methods ///
    performDownwardsRaycast(origin, targetUID) {
        return __awaiter(this, void 0, void 0, function* () {
            const direction = new Vector3(0, -1, 0);
            const raycastResponseMessage = yield this.connection.askByMessageType(new RequestRaycastClosestIntersectionMessage(convertToNumber3(origin), convertToNumber3(direction), { targetModelUIDs: [targetUID] }), ResponseRaycastClosestIntersectionMessage);
            const response = raycastResponseMessage.response;
            if (response === undefined)
                return undefined;
            return {
                hitPoint: new Vector3(...response.worldPosition),
                uid: response.uid
            };
        });
    }
    retrieveModelWorldTransform(modelUID) {
        const model = modelPool.getModel(modelUID);
        if (model === undefined)
            return undefined;
        return model.getWorldTransform();
    }
    /**
     * Computes the preview orientation by replacing the Yaw component of the initial orientation
     * with the controller's current world-space yaw. This correctly handles objects with any initial
     * rotation or pickup offset, since the Yaw reference is always the controller (offset-free).
     *
     * Formula: deltaYawQuat(currentControllerYaw - initialOrientationYaw) * initialOrientation
     */
    computeDropOffTransform(modelWorldTransform, hitPoint, initialOrientation, controllerUID) {
        modelWorldTransform.decompose(TEMP_UNUSED_POSITION, TEMP_ORIENTATION, TEMP_SCALE);
        // Current controller yaw (offset-free, pure controller rotation around world Y).
        const controllerTransform = this.retrieveModelWorldTransform(controllerUID);
        const currentControllerYaw = controllerTransform !== undefined
            ? TEMP_EULER.setFromQuaternion(TEMP_CONTROLLER_QUAT.setFromRotationMatrix(controllerTransform), 'YXZ').y
            : TEMP_EULER.setFromQuaternion(TEMP_ORIENTATION, 'YXZ').y;
        // Yaw baked into the initial orientation (scene-start rotation around world Y).
        const initialYaw = TEMP_EULER.setFromQuaternion(initialOrientation, 'YXZ').y;
        // Only the yaw difference is applied so pitch/roll of the initial orientation are preserved,
        // and the preview correctly faces the controller regardless of initial rotation or pickup offset.
        const deltaYaw = currentControllerYaw - initialYaw;
        const finalOrientation = TEMP_DELTA_YAW_QUAT.setFromAxisAngle(TEMP_VECTOR_Y, deltaYaw).multiply(initialOrientation);
        // Never return a shared matrix: left and right hand updates run concurrently and would otherwise
        // overwrite each other's preview pose.
        return new Matrix4().compose(hitPoint, finalOrientation.clone(), TEMP_SCALE);
    }
    getHalfBoundingHeight(uid) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // We use the aabb of the collision shape without any rotation applied. This relies on the fact that the model
                // was originally in an orientation that matches the orientation that we use to place it.
                const response = yield this.connection.askByMessageType(new RequestCollisionShapeAABBMessage(uid, true), ResponseCollisionShapeAABBMessage);
                const min = new Vector3().fromArray(response.min);
                const max = new Vector3().fromArray(response.max);
                const bounds = new Box3(min, max);
                const extent = bounds.getSize(new Vector3());
                return extent.y * 0.5;
            }
            catch (e) {
                console.error(e);
                return 0;
            }
        });
    }
}
